export interface TextLabelSizeOption {
  id: "xs" | "s" | "m" | "l" | "xl" | "2xl" | "3xl" | "4xl";
  label: "XS" | "S" | "M" | "L" | "XL" | "2XL" | "3XL" | "4XL";
  fontSize: number;
  title: string;
  description: string;
  sampleText: string;
  color: string;
  className: string;
}

export const TEXT_LABEL_SIZE_OPTIONS: readonly TextLabelSizeOption[] = [
  {
    id: "xs",
    label: "XS",
    fontSize: 20,
    title: "Footnote / micro label",
    description:
      "The smallest tier, for tiny inline annotations like trail markers or footnotes that should sit quietly on the map.",
    sampleText: "Bridge crossing",
    color: "#e6cfa3",
    className: "text-[clamp(0.85rem,1.5vw,1.2rem)]",
  },
  {
    id: "s",
    label: "S",
    fontSize: 28,
    title: "Note annotation",
    description:
      "Descriptive notes for roads, paths, and small landmarks. This is the quietest tier in the hierarchy.",
    sampleText: "Old road to the ruined watchtower",
    color: "#ead7b2",
    className: "text-[clamp(1.1rem,2vw,1.65rem)]",
  },
  {
    id: "m",
    label: "M",
    fontSize: 38,
    title: "Minor landmark",
    description:
      "A step up from notes for hamlets, small ruins, and minor features that warrant a visible label without competing with city names.",
    sampleText: "Hollow Mill",
    color: "#f0e0c4",
    className: "text-[clamp(1.45rem,2.5vw,2.3rem)]",
  },
  {
    id: "l",
    label: "L",
    fontSize: 48,
    title: "City label",
    description:
      "Default map text for settlements, landmarks, and points of interest. This is the standard readable working size.",
    sampleText: "Kingsport",
    color: "#f8f0dd",
    className: "text-[clamp(1.8rem,3vw,3rem)]",
  },
  {
    id: "xl",
    label: "XL",
    fontSize: 64,
    title: "Prominent landmark",
    description:
      "A stronger size for major destinations that need more emphasis than a standard city or point-of-interest label.",
    sampleText: "The Hollow Spire",
    color: "#f6ead2",
    className: "text-[clamp(2rem,3.5vw,3.4rem)]",
  },
  {
    id: "2xl",
    label: "2XL",
    fontSize: 80,
    title: "Region label",
    description:
      "Large area or territory names. This is the most prominent map text tier and should be used sparingly.",
    sampleText: "The Shrouded Marches",
    color: "#f5e6c8",
    className: "text-[clamp(2.2rem,4vw,4rem)] tracking-[0.04em]",
  },
  {
    id: "3xl",
    label: "3XL",
    fontSize: 96,
    title: "Massive region",
    description:
      "Extra-large territory or ocean names that need to read clearly when the map is zoomed out.",
    sampleText: "The Western Reach",
    color: "#f3e2bc",
    className: "text-[clamp(2.5rem,4.5vw,4.6rem)] tracking-[0.04em]",
  },
  {
    id: "4xl",
    label: "4XL",
    fontSize: 120,
    title: "Hero title",
    description:
      "Largest map lettering tier for campaign titles or sweeping labels; use rarely so the map stays legible.",
    sampleText: "The Shrouded Marches",
    color: "#f2dfba",
    className: "text-[clamp(2.8rem,5vw,5.2rem)] tracking-[0.04em]",
  },
] as const;

const TEXT_LABEL_FONT_SIZES = TEXT_LABEL_SIZE_OPTIONS.map((option) => option.fontSize);
const MIN_TEXT_LABEL_FONT_SIZE = TEXT_LABEL_FONT_SIZES[0] ?? 1;
const MAX_TEXT_LABEL_FONT_SIZE =
  TEXT_LABEL_FONT_SIZES[TEXT_LABEL_FONT_SIZES.length - 1] ?? MIN_TEXT_LABEL_FONT_SIZE;

export function getTextLabelSizeOptionByFontSize(fontSize: number | undefined): TextLabelSizeOption | null {
  return TEXT_LABEL_SIZE_OPTIONS.find((option) => option.fontSize === fontSize) ?? null;
}

export function clampTextLabelFontSize(fontSize: number): number {
  return Math.min(Math.max(fontSize, MIN_TEXT_LABEL_FONT_SIZE), MAX_TEXT_LABEL_FONT_SIZE);
}

export function getNearestTextLabelFontSize(fontSize: number): number {
  const clampedFontSize = clampTextLabelFontSize(fontSize);
  return TEXT_LABEL_FONT_SIZES.reduce((nearest, optionFontSize) => {
    if (Math.abs(optionFontSize - clampedFontSize) < Math.abs(nearest - clampedFontSize)) {
      return optionFontSize;
    }
    return nearest;
  }, TEXT_LABEL_FONT_SIZES[0] ?? clampedFontSize);
}
